'use client'
import React, { memo } from 'react'
import classNames from 'classnames'
import { Card } from '@dustin-web/product-components/src/cards/product-card-gallery-composable'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import { getProductLabels } from '@dustin-web/product-components/src/cards/utils'
import { useTrackingOnScreen } from '@dustin-web/microsite-components/src/on-screen'
import ProductPrice from '@dustin-web/product-components/src/product-price'
import EnergyLabel from '@dustin-web/product-components/src/energy-label'
import AddToCartButton from '@dustin-web/product-components/src/add-to-cart-button'
import type {
  DigitalDataOrigin,
  ProductWithCategoryType,
} from '@dustin-web/product-components/src/utils/digital-data-types'
import type { ProductTypeProps } from '@dustin-web/product-components/src/cards'
import type { Product } from '@productsite-beta/src/graphql/generated/graphql-types'

import './product-card.scss'

type Props = {
  imagePriority?: boolean
  product: Product
  ticket: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex: number
  showAddToCartButton?: boolean
  sizeClasses?: string
  className?: string
  isSmallScreen: boolean
  addToCartText: string
}

const ProductCard = ({
  product,
  ticket,
  imagePriority = false,
  digitalDataOrigin,
  digitalIndex,
  sizeClasses,
  className,
  isSmallScreen,
  addToCartText,
}: Props) => {
  const ref = useTrackingOnScreen(() => pushViewItemList(digitalIndex, digitalDataOrigin, product))

  if (!product) {
    return null
  }

  return (
    <div
      ref={ref}
      className={classNames(className, sizeClasses, 'product-upsell-card', {
        mobile: isSmallScreen,
      })}>
      <div className="wrapper">
        <Card.Picture
          className="product-image"
          imageWidth={56}
          imageHeight={56}
          product={product}
          ticket={ticket}
          imagePriority={imagePriority}
          digitalDataIndex={digitalIndex}
          digitalDataOrigin={digitalDataOrigin}
        />
        <div className="product-info">
          <div className="u-w-100">
            <Card.Title
              product={{ ...product, displaySpecifications: '' }}
              ticket={ticket}
              digitalDataIndex={digitalIndex}
              digitalDataOrigin={digitalDataOrigin}
              compact
              ellipsis
            />
            <EnergyLabel
              width={34}
              className="u-pt-8"
              textAlignment="right"
              productId={product.productErpIdentifier}
              nameSlug={product.nameSlug}
              productTitle={product.displayName || ''}
              energyClass={product.energyClass}
              energyLabelImageId={product.energyLabelImageId}
            />
            <div className="d-flex justify-content-between u-pt-12">
              <ProductPrice
                className="d-flex flex-wrap align-items-center"
                priceClassName="u-fs-16"
                campaignClassName="u-fs-16 u-mr-8 u-fw-lighter"
                price={product.price}
                displayVatInfo={false}
              />
              {isSmallScreen && (
                <AddToCartButton
                  quantity={1}
                  minWidth={false}
                  primary={false}
                  isAd={false}
                  className="flex-shrink-0"
                  product={product}
                  ticket={ticket}
                  addToCartText={addToCartText}
                  mainProductErpIdentifier={null}
                  digitalDataOrigin={digitalDataOrigin}
                  digitalDataIndex={digitalIndex}
                  showMiniBtn
                />
              )}
            </div>
            <Card.Availability
              compactStatus
              containerClassNames="product-availability"
              defaultContainerClassName="d-flex"
              availability={product.availability}
              isBid={product.price?.isBid}
            />
          </div>
          {!isSmallScreen && (
            <AddToCartButton
              shoppingCartIcon
              quantity={1}
              primary={false}
              minWidth={false}
              isAd={false}
              product={product}
              ticket={ticket}
              addToCartText={addToCartText}
              mainProductErpIdentifier={null}
              digitalDataOrigin={digitalDataOrigin}
              digitalDataIndex={digitalIndex}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function pushViewItemList(
  digitalIndex?: number,
  digitalDataOrigin?: DigitalDataOrigin,
  product?: ProductWithCategoryType | ProductTypeProps | null
) {
  if (product && digitalDataOrigin) {
    ProductDigitalData.pushViewItemList(
      [
        {
          ...product,
          productListIndex: digitalIndex,
          label: getProductLabels(product as ProductTypeProps),
        },
      ] as ProductWithCategoryType[],
      digitalDataOrigin
    )
  }
}

export default memo(ProductCard)
